// src/components/EngineerStatusCards.js
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Grid,
  Stack,
  CircularProgress,
} from "@mui/material";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { getStatusColor, getStatusIcon } from "../utils/statusHelpers";

function EngineerStatusCards() { // Removed BuildIcon import as it's now in statusHelpers
  const [engineerStatuses, setEngineerStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Query for active jobs (not completed or incomplete)
    const activeJobsQuery = query(
      collection(db, "jobs"),
      where("status", "in", ["Travelling", "On Site", "In Progress"])
    );

    const unsubscribe = onSnapshot(
      activeJobsQuery,
      (snapshot) => {
        const activeJobs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Group jobs by engineer
        const statusMap = {};
        activeJobs.forEach((job) => {
          if (job.engineerId && !statusMap[job.engineerId]) {
            statusMap[job.engineerId] = {
              engineerId: job.engineerId,
              engineerName: job.engineerName || "Unknown Engineer",
              job: job,
            };
          }
        });

        setEngineerStatuses(Object.values(statusMap));
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching engineer statuses:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (engineerStatuses.length === 0) {
    return (
      <Card
        sx={{
          background:
            "linear-gradient(135deg, rgba(148,163,184,0.1), rgba(15,23,42,0.95))",
          border: "1px solid rgba(148,163,184,0.2)",
        }}
      >
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">
            No engineers currently active
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Grid container spacing={2}>
      {engineerStatuses.map((status) => {
        const isOnSite =
          status.job.status === "On Site" ||
          status.job.status === "In Progress";

        return (
          <Grid item xs={12} md={6} lg={4} key={status.engineerId}>
            <Card
              sx={{
                background: isOnSite
                  ? "linear-gradient(135deg, rgba(16,185,129,0.15), rgba(15,23,42,0.95))"
                  : "linear-gradient(135deg, rgba(59,130,246,0.15), rgba(15,23,42,0.95))",
                border: `1px solid ${
                  isOnSite
                    ? "rgba(34,197,94,0.35)"
                    : "rgba(59,130,246,0.35)"
                }`,
                transition: "all 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              <CardContent>
                <Stack spacing={2}>
                  {/* Engineer Header */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: isOnSite ? "success.main" : "primary.main",
                        width: 48,
                        height: 48,
                      }}
                    >
                      <EngineeringIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {status.engineerName}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(status.job.status)}
                        label={status.job.status}
                        size="small"
                        color={getStatusColor(status.job.status)}
                      />
                    </Box>
                  </Box>

                  {/* Job Details */}
                  <Box
                    sx={{
                      bgcolor: "rgba(0,0,0,0.2)",
                      p: 1.5,
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      Current Job:
                    </Typography>
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        📋 {status.job.jobTemplateName || "Service"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        👤 {status.job.customerName || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        🚗 {status.job.vehicleReg || status.job.vehicle || "N/A"}
                      </Typography>
                      {status.job.siteAddress && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          📍 {status.job.siteAddress}
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  {/* Time Info */}
                  {status.job.onSiteTime && (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Typography
                        variant="caption"
                        color="success.main"
                        sx={{ fontWeight: 600 }}
                      >
                        ⏱ On site since{" "}
                        {new Date(
                          status.job.onSiteTime.toDate()
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

export default EngineerStatusCards;